import { Callout } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { CardsPanelActivity } from 'modules/cards/components/activity/CardsPanelActivity';
import { routeFor, routes } from 'src/core/constants/routes';
import { getIsFailedTransactionsEnabled } from 'src/core/selectors/globalSelectors';
import { AnalyticEventName } from 'src/core/utils/analytics';

import { MobileBanner } from './MobileBanner/MobileBanner';
import { MyCardOrderBanner } from './MyCardOrderBanner/MyCardOrderBanner';
import { PlasticCardControlSettings } from './PlasticCardControlSettings';
import { PlasticCardHeader } from './PlasticCardHeader';
import { RecardBanner } from './RecardBanner';
import { type ApiCard, type ApiCardSettings } from '../../../card';
import { CardFeedActivity } from '../../../components/CardFeedActivity';
import { RestrictedAccessWarning } from '../../../components/RestrictedAccessWarning';
import { type CardActivity } from '../../../hooks/useCardActivityQuery';
import type { CardLoad } from '../../../hooks/useCardLoadsQuery';
import type { CardPayment } from '../../../hooks/useCardPaymentsQuery';
import './PlasticCard.css';
import { Congrats } from '../../../order/components/Congrats';

type Props = {
  card: ApiCard;
  cardSettings: ApiCardSettings | undefined;
  cardActivity: CardActivity[] | undefined;
  cardPayments: CardPayment[] | undefined;
  cardLoads: CardLoad[] | undefined;
  selectedTransactionId?: string;
  refreshCard(): void;
  onCardReceptionConfirmationClick(): void;
};

export const PlasticCard = ({
  card,
  cardSettings,
  cardActivity,
  cardPayments,
  cardLoads,
  refreshCard,
  onCardReceptionConfirmationClick,
  selectedTransactionId,
}: Props) => {
  const { t } = useTranslation('global');
  const user = useUser();
  const company = useCompany();

  const [showCongrats, setShowCongrats] = useState(false);
  const isFailedTransactionsEnabled = useSelector(
    getIsFailedTransactionsEnabled,
  );

  if (showCongrats) {
    return <Congrats numberOfCardsOrdered={1} />;
  }

  const isContactlessEnabled =
    cardSettings?.authorisations.isContactlessEnabled;

  const isCompanyOnSfsAndCardOnBankable =
    company.banking_provider === 'sfs' && card.banking_provider === 'bankable';

  const displayRecardBanner =
    card.recard_request && !isCompanyOnSfsAndCardOnBankable;

  const displayMobileBanner =
    !displayRecardBanner &&
    !card.blocking_reason &&
    !user.has_device &&
    company.banking_provider === 'sfs' &&
    company.currency !== 'GBP' &&
    card.banking_provider === 'sfs';

  const getCardTransactionDetailsTargetUrl = (transactionId: string) => {
    return routeFor(routes.CARD_TRANSACTION_DETAILS.path, {
      company: company.id,
      transactionId,
    });
  };

  return (
    <div className="PlasticCard">
      <div className="PlasticCard__container">
        {displayRecardBanner && card.recard_request && (
          <ErrorBoundary context={{ scope: 'recard', team: 'cards-ux' }}>
            <RecardBanner
              company={company}
              recardRequest={card.recard_request}
            />
          </ErrorBoundary>
        )}

        <ErrorBoundary
          context={{
            scope: 'my-card::restricted-access-warning',
            team: 'cards-ux',
          }}
          fallbackComponent={<></>}
        >
          <div className="mb-24">
            <RestrictedAccessWarning
              cardBlockingReason={card.blocking_reason}
              isFullPage
            />
          </div>
        </ErrorBoundary>

        {card.cardOrder ? (
          <ErrorBoundary
            context={{
              scope: 'my-card::my-card-order-banner',
              team: 'banking-gateway',
            }}
          >
            <MyCardOrderBanner
              card={card}
              onCardReceptionConfirmationClick={
                onCardReceptionConfirmationClick
              }
              onReorderNotReceivedFormSuccessfulSubmission={() =>
                setShowCongrats(true)
              }
              className="mb-24"
            />
          </ErrorBoundary>
        ) : null}

        {card.status !== 'PRE' &&
          isContactlessEnabled &&
          card.transaction_count === 0 && (
            <Callout
              variant="info"
              title={t('cards.contactlessNotice')}
              className="mb-24"
            />
          )}
        {displayMobileBanner && <MobileBanner />}
        <PlasticCardHeader card={card} syncActivity={refreshCard} />
        {card.status !== 'PRE' && !card.deleted_at && (
          <>
            {cardSettings && (
              <PlasticCardControlSettings cardSettings={cardSettings} />
            )}
            {isFailedTransactionsEnabled && (
              <CardsPanelActivity
                cardId={card.id}
                clickEventName={AnalyticEventName.CARD_PANEL_NAVIGATION}
                selectedTransactionId={selectedTransactionId}
                title={
                  <h2 className="text-content-primary heading-l mt-32 mb-24">
                    {t('cards.recentActivity')}
                  </h2>
                }
                getCardTransactionDetailsTargetUrl={
                  getCardTransactionDetailsTargetUrl
                }
                isLarge
              />
            )}
            {!isFailedTransactionsEnabled &&
              cardActivity &&
              cardPayments &&
              cardLoads && (
                <>
                  <h2 className="text-content-primary heading-l mt-32 mb-24">
                    {t('cards.recentActivity')}
                  </h2>
                  <CardFeedActivity
                    cardActivity={cardActivity}
                    cardPayments={cardPayments}
                    cardLoads={cardLoads}
                  />
                </>
              )}
          </>
        )}
      </div>
    </div>
  );
};
